import React, {useRef, useState, useEffect} from 'react';
import {useHistory} from 'react-router-dom';
import {Button, Typography, NZAddressPicker, Checkbox, Error} from '@nzpost/react-components';
import CountrySelect from "./CountrySelect/CountrySelect";
import TextField from "@material-ui/core/TextField";
import CheckBoxOutlineBlankIcon from "@material-ui/icons/CheckBoxOutlineBlank";
import CheckBoxIcon from "@material-ui/icons/CheckBox";
import NewItemForm from "./NewItemForm";
import {customAlphabet} from 'nanoid';
import {useForm,  Controller, useFieldArray, useWatch} from "react-hook-form";

import {
  FormControl,
  FormGroup,
  FormHelperText,
  FormLabel, InputAdornment,
  MenuItem,
  Select,
  styled,
  TextareaAutosize
} from "@material-ui/core";
import PlusIcon from "./PlusIcon";
import WarningIcon from "./WarningIcon";
import isEmpty from 'lodash/isEmpty';
import get from 'lodash/get';
import Banner from "./Banner/Banner";
import {
  isValidEmail, 
  specialCharErrorMsg, 
  specialCharRegex, 
  specialCharValidationRule,
  otherCategoryDescriptionRegex
} from "./utils";
import ReactGA from "react-ga4";

const alphabet = '0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZ';
const nanoid = customAlphabet(alphabet, 10);

const parcelContentMenuItemStyle = {
  whiteSpace: 'normal',
  wordBreak: 'break-word',
  height: 'auto',
};

const EU_COUNTRY_CODES = [
  'AT', 'BE', 'BG', 'HR', 'CY', 'CZ', 'DK', 'EE', 'FI', 'FR', 'DE', 'GR',
  'HU', 'IE', 'IT', 'LV', 'LT', 'LU', 'MT', 'NL', 'PL', 'PT', 'RO', 'SK',
  'SI', 'ES', 'SE'
];

const TERMS_FIELDS = ['agreeTAndC1', 'agreeTAndC2', 'agreeTAndC3EU', 'agreeTAndCUSA'];

const MyCheckBox = ({checked, onClick}) => (
  <Checkbox
    icon={<CheckBoxOutlineBlankIcon fontSize="small"/>}
    checkedIcon={<CheckBoxIcon fontSize="small"/>}
    checked={checked}
    onClick={onClick}
    // inputProps={{'aria-label': 'select item'}}
  />
);

//Custom Step Icon styling (background circle)
const AutoSizeTextField = styled('div')(({ theme, ownerState }) => ({
  zIndex: 1,
  fill: '#fff',
  width: '32px',
  height: '32px',
  borderRadius: '50%',
  justifyContent: 'center',
  alignItems: 'center',
  display: 'flex',
  backgroundColor: theme.palette.secondary.main,
}));

//Custom Step Icon styling (background circle)
const StepLabelIconWrapper = styled('div')(({ theme, ownerState }) => ({
  zIndex: 1,
  fill: '#fff',
  width: '32px',
  height: '32px',
  borderRadius: '50%',
  justifyContent: 'center',
  alignItems: 'center',
  display: 'flex',
  backgroundColor: theme.palette.secondary.main,
}));

const CustomStepLabelIcon = ({ active, completed, className, icon }) => {
  return (
    <StepLabelIconWrapper ownerState={{ completed, active }} className={className}>
      <Typography variant='h4' color="highEmphasis">
        {icon}
      </Typography>
    </StepLabelIconWrapper>
  );
}

const defaultItem = {
  quantity: '',
  value: '',
  desc: '',
  weight: '',
  countryOrigin: ''
};
const makeDefaultItem = () => Object.assign({}, defaultItem, {id: window.self.crypto.randomUUID()});
const scrollTo = (el) => {
  if (el /* + other conditions */) {
    const offset = el.getBoundingClientRect().top + window.scrollY;
    window.scrollTo(0, offset - 180);
  }
}

const WizardPage = () => {
  const browserHistory = useHistory();
  const {
    watch,
    control,
    handleSubmit,
    formState: { errors, isSubmitted },
    setValue,
    setError,
    clearErrors,
    trigger
  } = useForm({
    trim: true,
    mode: "onChange",  // This triggers validation when fields lose focus
    defaultValues: {
      items: [makeDefaultItem()]
    }
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'items', // Array name
    keyName: 'id', // Unique key for each item
  });
  const currentCountry = get(useWatch({
    control,
    name: "toCountry",
    defaultValue: null
  }), 'country', '');

  const parcelContent = useWatch({
    control,
    name: "parcelContent",
    defaultValue: ''
  });

  const isUSOrTerritory = ['US', 'AS', 'GU', 'MP', 'PR', 'VI'].includes(currentCountry);
  const isEU = EU_COUNTRY_CODES.includes(currentCountry);

  const fromFullNameRef = useRef(null);
  const fromAddressRef = useRef(null);
  const fromPhoneRef = useRef(null);
  const toFullNameRef = useRef(null);
  const toCountryRef = useRef(null);
  const toAddressRef = useRef(null);
  const toSuburbRef = useRef(null);
  const toTownCityRef = useRef(null);
  const toStateProvinceRef = useRef(null);
  const toPostCodeRef = useRef(null);
  const toPhoneRef = useRef(null);
  const parcelContenRef = useRef(null);
  const itemsRef = useRef([]);
  const previousCountryRef = useRef('');
  const [countryHasValidation, setCountryHasValidation] = useState(false);

  // const [items, setItems] = useState([makeDefaultItem(),]);
  const [agreeTAndC1, setAgreeTAndC1] = useState(false);
  const [agreeTAndC2, setAgreeTAndC2] = useState(false);
  const [submissionHasFailed, setSubmissionHasFailed] = useState(false);
  // const [errors, setErrors] = useState({ items: [{}] });
  const [notification, setNotification] = useState(null);
  const [attemptedFourthSelection, setAttemptedFourthSelection] = useState(false);

  const items = watch('items');
  const mixedContentCodes = watch('mixedContentCodes');
  const itemCategorisationDescription = watch('itemCategorisationDescription');

  // Reset mixed content codes when user switches away from '991', and description when switching away from '999'
  useEffect(() => {
    if (parcelContent !== '991') {
      if (mixedContentCodes && mixedContentCodes.length > 0) {
        setValue('mixedContentCodes', []);
      }
      setAttemptedFourthSelection(false);
    }
    if (parcelContent !== '999') {
      if (itemCategorisationDescription) {
        setValue('itemCategorisationDescription', '');
      }
    }
  }, [parcelContent, setValue, mixedContentCodes, itemCategorisationDescription]);

  // Clear attempted fourth selection warning when user deselects below 3 items
  useEffect(() => {
    if (mixedContentCodes && mixedContentCodes.length < 3) {
      setAttemptedFourthSelection(false);
    }
  }, [mixedContentCodes]);

  useEffect(() => {
    console.log('currentCountry changed:', {
      currentCountry,
      previousRef: previousCountryRef.current
    });
    if (previousCountryRef.current !== currentCountry) {
      console.log('Country changed, resetting parcelContent and mixedContentCodes');
      setValue('parcelContent', '', {
        shouldValidate: false,
        shouldDirty: false,
      });
      setValue('mixedContentCodes', [], {
        shouldValidate: false,
        shouldDirty: false,
      });
      TERMS_FIELDS.forEach((name) => {
        setValue(name, false, { shouldValidate: false, shouldDirty: false });
      });
      clearErrors(TERMS_FIELDS);
    }

    previousCountryRef.current = currentCountry;
  }, [currentCountry, setValue, clearErrors]);

  //accumulate weights, isNaN not great at detecting absence of number when empty string
  let totalWeight = items.reduce((acc, currentVal) => {
    let weight = parseFloat(currentVal.weight);
    let quantity = parseFloat(currentVal.quantity);
    if (isNaN(weight)) weight = 0;
    if (isNaN(quantity)) quantity = 0;
    return acc + (weight * quantity);
  }, 0).toFixed(3);

  let totalValue = items.reduce((acc, currentVal) => {
    let value = parseFloat(currentVal.value);
    let quantity = parseFloat(currentVal.quantity);
    if (isNaN(value)) value = 0;
    if (isNaN(quantity)) quantity = 0;
    return acc + (value * quantity);
  }, 0).toFixed(2);

  useEffect(() => {

    if (isEmpty(currentCountry)) return;

    // Request (8) (GET https://uat.apis.nzpost.co.nz/international-validation-process-1-0/api/ead/country-has-validation/US)
    fetch(`${process.env.REACT_APP_PROXY_BASE_URL}/api/international-validation/country-has-validation/${currentCountry}`, {
      "method": "GET",
    })
      .then((res) => res.json())
      .then(json => {
        const errors = get(json, 'sucesss', null);
        const success = get(json, 'sucesss', false);
        const hasValidation = get(json, 'country_has_validation', false);

        if (!success) {
          setCountryHasValidation(hasValidation);
        }

      })
      .catch(console.error.bind(console));

  }, [currentCountry]);

  const onSubmitSuccess = (data) => {
    const {
      fromFullName,
      fromAddress,
      fromPhoneNumber,
      fromEmail,
      rFullName,
      rCompanyName,
      toCountry,
      rAddress1,
      rbuildingName,
      rSuburb,
      rTown,
      rStateProvince,
      rPostcode,
      rPhoneNumber,
      rEmail,
      rTaxNb,
      parcelContent,
      items,
      itemCategorisationDescription
    } = data;

    setNotification(null);

    const addressId = get(fromAddress, 'address_id', null);
    if (!addressId) {
      console.log('Could not lookup address_id from the selected address')
      return;
    }

    fetch(`${process.env.REACT_APP_PROXY_BASE_URL}/api/parceladdress/domestic/addresses/${addressId}`, {
      "method": "GET",
      "headers": {
        "Content-Type": "application/json"
    }}).then(res => res.ok? res.json():null).then(json => {
      if (!json) return;
      const success = get(json, 'success', false);
      const senderAddress = get(json, 'address', null);

      if (!success || !senderAddress) {
        return;
      }

      const senderStreetNb = get(senderAddress, 'street_number', '')+get(senderAddress, 'street_alpha', '');
      const senderStreet = get(senderAddress, 'street', '') + ' ' + get(senderAddress, 'street_type', '');
      // const receiverStreetNb = rAddress1.split(' ')[0];
      // const receiverStreet = rAddress1.split(' ').slice(1).join(' ');
      const countryCode = get(toCountry, 'country', '');
      const natureOfTransactionCode = parseInt(parcelContent);

      let manualTarifCount = 0;

      const parcelContents = items.map((item, i) => {
        const isManualTarifCode = !isEmpty(get(item, 'manualTarifCode', ""));
        const itemWeight = parseFloat( get(item, 'weight', 0));
        const itemValue = parseFloat( get(item, 'value', 0));
        const itemQuantity = parseFloat(get(item,'quantity', 0));
        const itemTarifCode = get(item,'tarifCode', '');
        let payload = {
          "content_number": i+1,
          "description": item.description,
          "quantity": itemQuantity ,
          "value": itemValue,
          "weight_kg": itemWeight,
          "country_code": get(item, 'countryOrigin.country', '')
        };
        if (itemTarifCode) {
          payload = Object.assign(payload, {
            "harmonised_system_tariff": itemTarifCode
          })
        }
        if (isManualTarifCode) {
          manualTarifCount++;
        }
        return payload;
      });

      const parcelDetailsPayload = {
        "currency": "NZD",
        "nature_of_transaction_code": natureOfTransactionCode,
        "parcel_contents": parcelContents
      };

      // Add mixed content codes if selected
      if (parcelContent === '991' && mixedContentCodes && mixedContentCodes.length > 0) {
        parcelDetailsPayload.nature_of_transaction_mixed_content_codes = mixedContentCodes;
      }

      // Add description if "Other" is selected
      if (parcelContent === '999' && itemCategorisationDescription) {
        parcelDetailsPayload.nature_of_transaction_description = itemCategorisationDescription;
      }

      fetch(`${process.env.REACT_APP_PROXY_BASE_URL}/api2/consignment/order`, {
        "method": "POST",
        "headers": {
          "Content-Type": "application/json"
        },
        "body": JSON.stringify({
          "delivery_address": {
            "country_code": countryCode,
            "building_name": rbuildingName,
            "street_number": "",
            "street": rAddress1,
            "suburb": rSuburb,
            "city": rTown,
            "state": rStateProvince,
            "postcode": rPostcode
          },
          "parcel_details": parcelDetailsPayload,
          "receiver_details": {
            "name": rFullName,
            "email": rEmail,
            "phone": rPhoneNumber,
            "vat_number": rTaxNb,
            "company_name": rCompanyName
          },
          "sender_details": {
            "name": fromFullName,
            "email": fromEmail,
            "phone": fromPhoneNumber
          },
          "sender_address": {
            "street_number": senderStreetNb,
            "street": senderStreet,
            "suburb": get(senderAddress, 'suburb', ''),
            "city": get(senderAddress, 'city', ''),
            "state": get(senderAddress, 'toStateProvince', ''),
            "postcode": get(senderAddress, 'postcode', ''),
            "country_code": "NZ"
          }
        })
      })
        .then(res => res.ok ? res.json():null)
        .then(json => {
          const success = get(json, 'success', false);
          const orderId = get(json, 'data.order_id', null);
          if (!success || !orderId) {
            setSubmissionHasFailed(true);
            return;
          }
          ReactGA.event({
            category: "das_completed_form_success",
            action: "das_completed_form_success",
            label: `Complete form click success`,
            itemsCount: items.length,
            manualTarifCount
          });
          browserHistory.push(`/success/${orderId}`);
          setTimeout(() => {
            window.scrollTo({
              top: 0,
              left: 0,
              behavior: "smooth",
            });
          }, 200);
        }).catch(err => {
          ReactGA.event({
            category: "das_completed_form_api_error",
            action: "das_completed_form_api_error",
            label: `Complete form click API error(s)`,
          });
          console.log(err);
      });
    });
  }

  const onSubmitError = (errors) => {

    ReactGA.event({
      category: "das_completed_form_error",
      action: "das_completed_form_error",
      label: `Complete form click error(s)`
    });

    if (Object.keys(errors).length > 0) {
      setNotification({msg: "Please complete all of the required fields.", code: "ERROR"});
      // Find the first error and scroll to it
      const allErrors = Object.keys(errors);
      if (!Array.isArray(allErrors)) return;
      if (allErrors.includes('fromFullName') && fromFullNameRef.current) {
        fromFullNameRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
      } else if (allErrors.includes('fromAddress') && fromAddressRef.current) {
        fromAddressRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
      } else if (allErrors.includes('fromPhoneNumber') && fromPhoneRef.current) {
        fromPhoneRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
      } else if (allErrors.includes('rFullName') && toFullNameRef.current) {
        toFullNameRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
      } else if (allErrors.includes('toCountry') && toCountryRef.current) {
        toCountryRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
      } else if (allErrors.includes('rAddress1') && toAddressRef.current) {
        toAddressRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
      } else if (allErrors.includes('rSuburb') && toSuburbRef.current) {
        toSuburbRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
      } else if (allErrors.includes('rTown') && toTownCityRef.current) {
        toTownCityRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
      } else if (allErrors.includes('rStateProvince') && toStateProvinceRef.current) {
        toStateProvinceRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
      } else if (allErrors.includes('rPostcode') && toPostCodeRef.current) {
        toPostCodeRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
      } else if (allErrors.includes('rPhoneNumber') && toPhoneRef.current) {
        toPhoneRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
      } else if (allErrors.includes("parcelContent") && parcelContenRef.current) {
        parcelContenRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
      } else if ( allErrors.includes("items") && errors.items.length > 0 && itemsRef.current.length > 0) {
        itemsRef.current[ Object.keys(errors.items)[0] ].scrollIntoView({ behavior: 'smooth', block: 'center' });
      } else if (
        allErrors.includes("agreeTAndC1")
        || allErrors.includes("agreeTAndC2")
        || allErrors.includes("agreeTAndC3EU")
        || allErrors.includes("agreeTAndCUSA")
      ) {
          setNotification({msg: "Please agree to the terms and conditions.", code: "ERROR"});
      }
    }
  };

  const fromFullName = watch("fromFullName");

  return (
    <div id="nzpost-web-eship-prototype" className="root-wrapper">
      <div className="notifBanner">
        {notification &&
          <Banner notification={notification} additionalStyle="bannerContainer" onClose={() => setNotification(null)}/>}
      </div>
      <div className="mainTitle h1">
        Complete your Customs forms online
      </div>
      <div className="mainSubtitle body1">
        Sending internationally from an NZ Post store? You can complete your consignment note & Customs declaration
        here.
      </div>
      <div className="notice1 body1">
        <span className="h5">All fields are required</span> unless marked as optional.
      </div>
      <div className="notice2 body1">
        Once you complete the form, go to the counter. The NZ Post agent will help you select your sending service and
        pay.
      </div>

      <div className="notice3">
        <div className="icon-container">
          <span className="icon">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
              <path fillRule="evenodd" clipRule="evenodd" d="M12 4C7.589 4 4 7.589 4 12C4 16.411 7.589 20 12 20C16.411 20 20 16.411 20 12C20 7.589 16.411 4 12 4ZM12 22C6.486 22 2 17.514 2 12C2 6.486 6.486 2 12 2C17.514 2 22 6.486 22 12C22 17.514 17.514 22 12 22ZM12.0004 17.7998C11.5584 17.7998 11.2004 17.4418 11.2004 16.9998V9.99983C11.2004 9.55883 11.5584 9.19983 12.0004 9.19983C12.4414 9.19983 12.7994 9.55883 12.7994 9.99983L12.8004 16.9998C12.8004 17.4418 12.4424 17.7998 12.0004 17.7998ZM12 6C11.448 6 11 6.448 11 7C11 7.552 11.448 8 12 8C12.552 8 13 7.552 13 7C13 6.448 12.552 6 12 6Z" fill="#0056F4"/>
            </svg>
          </span>
        </div>
        <div className="content">
          <div className="message">
            Sending to the United States and Puerto Rico
          </div>
          <div className="body1">
            Items sent to the United States and Puerto Rico will incur additional taxes and fees (gifts valued up to $150NZD and letters/documents with no commercial value are exempt).
          </div>
          <div className="body1">
            For more information, ask in store or <a href="https://nzp.st/US-Sending" target="_blank" className="link">see our website</a>.
          </div>
        </div>
      </div>

      <form onSubmit={handleSubmit(onSubmitSuccess, onSubmitError)}>

        <div className="yourDetails">
          <div className="sectionTitle h2">
            Your details
          </div>
          <div className="body1 fullNameHelper">
            The name you enter here will be used as your digital signature.
          </div>
          <Controller
            name='fromFullName'
            control={control}
            rules={{
              required: "Please enter your full name.",
              validate: specialCharValidationRule

            }}
            render={({field, fieldState: {error}}) => (
              <TextField
                {...field}
                onChange={(e) => {
                  field.onChange(e);
                  setValue('fromFullName', e.target.value.trimStart());
                }}
                inputRef={fromFullNameRef}
                color='secondary'
                margin='none'
                variant='outlined'
                inputProps={{maxLength: 40}}
                fullWidth
                label='Full name'
                error={!!error}
                helperText={get(error, 'message')}
              />
            )}
          />

          <Controller
            name='fromAddress'
            control={control}
            rules={{
              required: "Please select an address.",
            }}
            render={({field, fieldState: {error}}) => {
              return (
                <NZAddressPicker
                  {...field}
                  inputRef={fromAddressRef}
                  label='Address'
                  margin='none'
                  clicked={true}
                  parcelAddressBaseURL={
                    `${process.env.REACT_APP_PROXY_BASE_URL}/api/parceladdress`
                  }
                  error={!!error}
                  helperText={get(error, 'message')}
                />
              );
            }}
          />
          <Controller
            name='fromPhoneNumber'
            control={control}
            rules={{
              required: "Please enter your phone number.",
              validate: specialCharValidationRule
            }}
            render={({field, fieldState: {error}}) => (
              <TextField
                {...field}
                onChange={(e) => {
                  field.onChange(e);
                  setValue('fromPhoneNumber', e.target.value.trimStart());
                }}
                inputRef={fromPhoneRef}
                color='secondary'
                margin='none'
                variant='outlined'
                inputProps={{maxLength: 20}}
                fullWidth
                label='Phone number'
                type="tel"
                error={!!error}
                helperText={get(error, 'message')}
              />
            )}
          />

          <Controller
            name='fromEmail'
            control={control}
            rules={{
              required: "Please enter your email address.",
              validate: isValidEmail
            }}
            render={({field, fieldState: {error}}) => (
              <TextField
                {...field}
                onChange={(e) => {
                  field.onChange(e);
                  setValue('fromEmail', e.target.value.trimStart());
                }}
                color='secondary'
                margin='none'
                variant='outlined'
                inputProps={{maxLength: 254}}
                fullWidth
                label='Email address'
                error={!!error}
                helperText={get(error, 'message')}
              />
            )}
          />
        </div>

        <div className="receiverDetails">
          <div className="sectionTitle h2">
            Receiver's details
          </div>

          <Controller
            name='rFullName'
            control={control}
            rules={{
              required: "Please enter the receiver's full name.",
              validate: specialCharValidationRule
            }}
            render={({field, fieldState: {error}}) => (
              <TextField
                {...field}
                onChange={(e) => {
                  field.onChange(e);
                  setValue('rFullName', e.target.value.trimStart());
                }}
                inputRef={toFullNameRef}
                color='secondary'
                margin='none'
                variant='outlined'
                inputProps={{maxLength: 40}}
                fullWidth
                label='Full name'
                error={!!error}
                helperText={get(error, 'message')}
              />
            )}
          />

          <Controller
            name='rCompanyName'
            control={control}
            rules={{
              validate: specialCharValidationRule
            }}
            render={({field, fieldState: {error}}) => (
              <TextField
                {...field}
                onChange={(e) => {
                  field.onChange(e);
                  setValue('rCompanyName', e.target.value.trimStart());
                }}
                color='secondary'
                margin='none'
                variant='outlined'
                inputProps={{maxLength: 40}}
                fullWidth
                label='Company name (optional)'
                error={!!error}
                helperText={get(error, 'message')}
              />
            )}
          />

          <Controller
            name='toCountry'
            control={control}
            rules={{
              required: "Please select a destination country.",
            }}
            render={({field, fieldState: {error}}) => {
              return (
                <CountrySelect
                  {...field}
                  onChange={(v) => {
                    field.onChange(v);

                    TERMS_FIELDS.forEach((name) => {
                      setValue(name, false, { shouldValidate: false, shouldDirty: false });
                    });
                    clearErrors(TERMS_FIELDS);
                  }}
                  defaultValue={'hello world too'}
                  error={!!error}
                  helperText={get(error, 'message')}
                  inputRef={toCountryRef}
                  label="Country"
                  margin='none'
                  clicked={true}
                  requiresConfirmation={true}
                  // name='toCountry'
                  // onCountrySelected={(c) => console.log(c)}
                  // helperText={errors['toCountry'] && "Please select a destination country."}
                  // error={errors['toCountry']}
                  // onBlur={strLiveValidation("toCountry", toCountry, errors)}
                />
              )
            }}
          />

          {isEU && <div className="notice5">
            <div className="icon-container">
          <span className="icon">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
              <path fillRule="evenodd" clipRule="evenodd" d="M12 4C7.589 4 4 7.589 4 12C4 16.411 7.589 20 12 20C16.411 20 20 16.411 20 12C20 7.589 16.411 4 12 4ZM12 22C6.486 22 2 17.514 2 12C2 6.486 6.486 2 12 2C17.514 2 22 6.486 22 12C22 17.514 17.514 22 12 22ZM12.0004 17.7998C11.5584 17.7998 11.2004 17.4418 11.2004 16.9998V9.99983C11.2004 9.55883 11.5584 9.19983 12.0004 9.19983C12.4414 9.19983 12.7994 9.55883 12.7994 9.99983L12.8004 16.9998C12.8004 17.4418 12.4424 17.7998 12.0004 17.7998ZM12 6C11.448 6 11 6.448 11 7C11 7.552 11.448 8 12 8C12.552 8 13 7.552 13 7C13 6.448 12.552 6 12 6Z" fill="#0056F4"/>
            </svg>
          </span>
            </div>
            <div className="content">
              <div className="body1">
                Items sent to countries in the European Union are subject to additional taxes, duties, or charges. If these are not prepaid by the sender, the receiver must pay for them. <a target="_blank" href="https://www.nzpost.co.nz/contact-support/international-delivery-updates#eu" className="link">Find out more.</a>
              </div>
            </div>
          </div>}

          <Controller
            name='rAddress1'
            control={control}
            rules={{
              required: "Please enter the receiver’s address.",
              validate: specialCharValidationRule
            }}
            render={({field, fieldState: {error}}) => (
              <TextField
                {...field}
                onChange={(e) => {
                  field.onChange(e);
                  setValue('rAddress1', e.target.value.trimStart());
                }}
                inputRef={toAddressRef}
                color='secondary'
                margin='normal'
                variant='outlined'
                inputProps={{maxLength: 40}}
                fullWidth
                label='Address'
                error={!!error}
                helperText={get(error, 'message')}
              />
            )}
          />

          <Controller
            name='rbuildingName'
            control={control}
            rules={{
              validate: specialCharValidationRule
            }}
            render={({field, fieldState: {error}}) => (
              <TextField
                {...field}
                onChange={(e) => {
                  field.onChange(e);
                  setValue('rbuildingName', e.target.value.trimStart());
                }}
                color='secondary'
                margin='none'
                variant='outlined'
                inputProps={{maxLength: 40}}
                fullWidth
                label='Building name (optional)'
                error={!!error}
                helperText={get(error, 'message')}
              />
            )}
          />

          <Controller
            name='rSuburb'
            control={control}
            rules={{
              required: "Please enter the receiver’s suburb.",
              validate: specialCharValidationRule
            }}
            render={({field, fieldState: {error}}) => (
              <TextField
                {...field}
                onChange={(e) => {
                  field.onChange(e);
                  setValue('rSuburb', e.target.value.trimStart());
                }}
                inputRef={toSuburbRef}
                color='secondary'
                margin='none'
                variant='outlined'
                inputProps={{maxLength: 40}}
                fullWidth
                label='Suburb'
                error={!!error}
                helperText={get(error, 'message')}
              />
            )}
          />

          <Controller
            name='rTown'
            control={control}
            rules={{
              required: "Please enter the receiver’s town/city.",
              validate: specialCharValidationRule
            }}
            render={({field, fieldState: {error}}) => (
              <TextField
                {...field}
                onChange={(e) => {
                  field.onChange(e);
                  setValue('rTown', e.target.value.trimStart());
                }}
                inputRef={toTownCityRef}
                color='secondary'
                margin='none'
                variant='outlined'
                inputProps={{maxLength: 40}}
                fullWidth
                maxLength="40"
                label='Town/City'
                error={!!error}
                helperText={get(error, 'message')}
              />
            )}
          />

          <Controller
            name='rStateProvince'
            control={control}
            rules={{
              // required: "Please enter the receiver’s state/province.",
              validate: specialCharValidationRule
            }}
            render={({field, fieldState: {error}}) => (
              <TextField
                {...field}
                onChange={(e) => {
                  field.onChange(e);
                  setValue('rStateProvince', e.target.value.trimStart());
                }}
                inputRef={toStateProvinceRef}
                color='secondary'
                margin='none'
                variant='outlined'
                inputProps={{maxLength: 35}}
                fullWidth
                label='State/Province'
                error={!!error}
                helperText={get(error, 'message') || "Leave empty if the receiving country doesn’t have states or provinces."}
              />
            )}
          />

          <Controller
            name='rPostcode'
            control={control}
            rules={{
              required: "Please enter the receiver’s postcode.",
              validate: specialCharValidationRule
            }}
            render={({field, fieldState: {error}}) => (
              <TextField
                {...field}
                onChange={(e) => {
                  field.onChange(e);
                  setValue('rPostcode', e.target.value.trimStart());
                }}
                inputRef={toPostCodeRef}
                color='secondary'
                margin='none'
                variant='outlined'
                inputProps={{maxLength: 17}}
                fullWidth
                label='Postcode'
                error={!!error}
                helperText={get(error, 'message') || "For addresses with no postcode, enter 0000."}
              />
            )}
          />

          <Controller
            name='rPhoneNumber'
            control={control}
            rules={{
              required: "Please enter the receiver’s Phone number.",
              validate: specialCharValidationRule
            }}
            render={({field, fieldState: {error}}) => (
              <TextField
                {...field}
                onChange={(e) => {
                  field.onChange(e);
                  setValue('rPhoneNumber', e.target.value.trimStart());
                }}
                inputRef={toPhoneRef}
                color='secondary'
                margin='none'
                variant='outlined'
                inputProps={{maxLength: 20}}
                type="tel"
                fullWidth
                label='Phone number'
                error={!!error}
                helperText={get(error, 'message')}
              />
            )}
          />

          <Controller
            name='rEmail'
            control={control}
            rules={{
              required: isUSOrTerritory && "Please enter the receiver’s email address.",
              validate: isValidEmail
            }}
            render={({field, fieldState: {error}}) => (
              <TextField
                {...field}
                onChange={(e) => {
                  field.onChange(e);
                  setValue('rEmail', e.target.value.trimStart());
                }}
                color='secondary'
                margin='none'
                variant='outlined'
                inputProps={{maxLength: 254}}
                fullWidth
                label={isUSOrTerritory ? 'Email address':'Email address (optional)'}
                error={!!error}
                helperText={get(error, 'message')}
              />
            )}
          />

          {isUSOrTerritory && <div className="notice4">
            <div className="icon-container">
          <span className="icon">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
              <path fillRule="evenodd" clipRule="evenodd" d="M12 4C7.589 4 4 7.589 4 12C4 16.411 7.589 20 12 20C16.411 20 20 16.411 20 12C20 7.589 16.411 4 12 4ZM12 22C6.486 22 2 17.514 2 12C2 6.486 6.486 2 12 2C17.514 2 22 6.486 22 12C22 17.514 17.514 22 12 22ZM12.0004 17.7998C11.5584 17.7998 11.2004 17.4418 11.2004 16.9998V9.99983C11.2004 9.55883 11.5584 9.19983 12.0004 9.19983C12.4414 9.19983 12.7994 9.55883 12.7994 9.99983L12.8004 16.9998C12.8004 17.4418 12.4424 17.7998 12.0004 17.7998ZM12 6C11.448 6 11 6.448 11 7C11 7.552 11.448 8 12 8C12.552 8 13 7.552 13 7C13 6.448 12.552 6 12 6Z" fill="#0056F4"/>
            </svg>
          </span>
            </div>
            <div className="content">
              <div className="message">
                Receiver email address is required when sending to the United States and US territories. The receiver may be contacted regarding payment of duties and taxes.
              </div>
              <div className="message">
                If you don’t know the receiver’s email address, use your own.
              </div>
            </div>
          </div>}

          <Controller
            name='rTaxNb'
            control={control}
            rules={{
              validate: specialCharValidationRule
            }}
            render={({field, fieldState: {error}}) => (
              <TextField
                {...field}
                onChange={(e) => {
                  field.onChange(e);
                  setValue('rTaxNb', e.target.value.trimStart());
                }}
                color='secondary'
                margin='none'
                variant='outlined'
                inputProps={{maxLength: 25}}
                fullWidth
                label='Tax number (optional)'
                error={!!error}
                helperText={get(error, 'message')}
              />
            )}
          />


        </div>
        <div className="parcelsContent">
          <div className="sectionTitle h2">
            Parcel contents
          </div>
          <FormControl ref={parcelContenRef} error={!!errors.parcelContent} fullWidth>
            <div className="sectionSubTitle h3">
              Which category best describes the items in your parcel?
            </div>
            <Controller
              name="parcelContent"
              control={control}
              rules={{required: 'Please select an option'}}
              render={({field}) => {
                console.log('parcelContent field value:', field.value);
                return (
                <Select
                  {...field}
                  value={field.value ? field.value : ''}
                  displayEmpty
                  variant="outlined"
                  inputProps={{'aria-label': 'parcel content category'}}
                >
                  <MenuItem style={parcelContentMenuItemStyle} value="" disabled>Select a category</MenuItem>
                  <MenuItem style={parcelContentMenuItemStyle} value="31">Gift (non-commercial)</MenuItem>
                  <MenuItem style={parcelContentMenuItemStyle} value="91">Documents</MenuItem>
                  <MenuItem disabled={isEU} style={parcelContentMenuItemStyle} value="32">Commercial sample</MenuItem>
                  <MenuItem style={parcelContentMenuItemStyle} value="21">Returned goods</MenuItem>
                  <MenuItem disabled={isEU}  style={parcelContentMenuItemStyle} value="12">E‑commerce goods (business to consumer)</MenuItem>
                  <MenuItem disabled={isEU}  style={parcelContentMenuItemStyle} value="111">Commercial sale of goods (business to business)</MenuItem>
                  <MenuItem style={parcelContentMenuItemStyle} value="991">Mixed content</MenuItem>
                  <MenuItem style={parcelContentMenuItemStyle} value="999">Other</MenuItem>
                </Select>
              )
              }}
            />

            {errors.parcelContent && <FormHelperText>{errors.parcelContent.message}</FormHelperText>}
          </FormControl>

          {parcelContent === '991' && (
            <FormControl fullWidth error={!!errors.mixedContentCodes} margin="normal">
              <div className="h4" style={{ marginBottom: '8px' }}>
                Select the content categories of your parcel
              </div>
              <div className="body1" style={{ marginBottom: '12px' }}>
                {(mixedContentCodes || []).length} of 3 selected
              </div>
              <FormGroup>
                <Controller
                  name="mixedContentCodes"
                  control={control}
                  rules={{
                    validate: (val) => {
                      if (!val || val.length === 0) return 'Select the content categories in your parcel.';
                      return true;
                    }
                  }}
                  render={({ field }) => (
                    <>
                      <div style={{ display: 'flex', alignItems: 'center', marginBottom: '8px' }}>
                        <Checkbox
                          icon={<CheckBoxOutlineBlankIcon fontSize="small" />}
                          checkedIcon={<CheckBoxIcon fontSize="small" />}
                          checked={(field.value || []).includes('31')}
                          onChange={(e) => {
                            const current = field.value || [];
                            if (e.target.checked) {
                              if (current.length < 3) {
                                field.onChange([...current, '31']);
                                setAttemptedFourthSelection(false);
                              } else {
                                setAttemptedFourthSelection(true);
                              }
                            } else {
                              field.onChange(current.filter(code => code !== '31'));
                            }
                          }}
                          inputProps={{ 'aria-label': 'Gift (non-commercial)' }}
                        />
                        <span style={{ marginLeft: '8px' }}>Gift (non-commercial)</span>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', marginBottom: '8px' }}>
                        <Checkbox
                          icon={<CheckBoxOutlineBlankIcon fontSize="small" />}
                          checkedIcon={<CheckBoxIcon fontSize="small" />}
                          checked={(field.value || []).includes('91')}
                          onChange={(e) => {
                            const current = field.value || [];
                            if (e.target.checked) {
                              if (current.length < 3) {
                                field.onChange([...current, '91']);
                              } else {
                                setAttemptedFourthSelection(true);
                              }
                            } else {
                              field.onChange(current.filter(code => code !== '91'));
                            }

                          }}
                          inputProps={{ 'aria-label': 'Documents' }}
                        />
                        <span style={{ marginLeft: '8px' }}>Documents</span>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', marginBottom: '8px' }}>
                        <Checkbox
                          disabled={isEU}
                          icon={<CheckBoxOutlineBlankIcon fontSize="small" />}
                          checkedIcon={<CheckBoxIcon fontSize="small" />}
                          checked={(field.value || []).includes('32')}
                          onChange={(e) => {
                            const current = field.value || [];
                            console.log("e.target.checked = ", e.target.checked)
                            if (e.target.checked) {
                              if (current.length < 3) {
                                field.onChange([...current, '32']);
                              } else {
                                setAttemptedFourthSelection(true);
                              }
                            } else {
                              field.onChange(current.filter(code => code !== '32'));
                            }
                          }}
                          inputProps={{ 'aria-label': 'Commercial sample' }}
                        />
                        <span style={{ marginLeft: '8px', opacity: isEU ? 0.5 : 1 }}>Commercial sample</span>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', marginBottom: '8px' }}>
                        <Checkbox
                          icon={<CheckBoxOutlineBlankIcon fontSize="small" />}
                          checkedIcon={<CheckBoxIcon fontSize="small" />}
                          checked={(field.value || []).includes('21')}
                          onChange={(e) => {
                            const current = field.value || [];
                            if (e.target.checked) {
                              if (current.length < 3) {
                                field.onChange([...current, '21']);
                                setAttemptedFourthSelection(false);
                              } else {
                                setAttemptedFourthSelection(true);
                              }
                            } else {
                              field.onChange(current.filter(code => code !== '21'));
                            }
                          }}
                          inputProps={{ 'aria-label': 'Returned goods' }}
                        />
                        <span style={{ marginLeft: '8px' }}>Returned goods</span>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', marginBottom: '8px' }}>
                        <Checkbox
                          disabled={isEU}
                          icon={<CheckBoxOutlineBlankIcon fontSize="small" />}
                          checkedIcon={<CheckBoxIcon fontSize="small" />}
                          checked={(field.value || []).includes('12')}
                          onChange={(e) => {
                            const current = field.value || [];
                            if (e.target.checked) {
                              if (current.length < 3) {
                                field.onChange([...current, '12']);
                                setAttemptedFourthSelection(false);
                              } else {
                                setAttemptedFourthSelection(true);
                              }
                            } else {
                              field.onChange(current.filter(code => code !== '12'));
                            }
                          }}
                          inputProps={{ 'aria-label': 'E-commerce goods (business to consumer)' }}
                        />
                        <span style={{ marginLeft: '8px', opacity: isEU ? 0.5 : 1 }}>E-commerce goods (business to consumer)</span>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', marginBottom: '8px' }}>
                        <Checkbox
                          disabled={isEU}
                          icon={<CheckBoxOutlineBlankIcon fontSize="small" />}
                          checkedIcon={<CheckBoxIcon fontSize="small" />}
                          checked={(field.value || []).includes('111')}
                          onChange={(e) => {
                            const current = field.value || [];
                            if (e.target.checked) {
                              if (current.length < 3) {
                                field.onChange([...current, '111']);
                                setAttemptedFourthSelection(false);
                              } else {
                                setAttemptedFourthSelection(true);
                              }
                            } else {
                              field.onChange(current.filter(code => code !== '111'));
                            }
                          }}
                          inputProps={{ 'aria-label': 'Commercial sale of goods (business to business)' }}
                        />
                        <span style={{ marginLeft: '8px', opacity: isEU ? 0.5 : 1 }}>Commercial sale of goods (business to business)</span>
                      </div>
                    </>
                  )}
                />
              </FormGroup>
              {attemptedFourthSelection && (
                <div style={{ display: 'flex', alignItems: 'anchor-center', gap: '8px', marginTop: '8px', color: "#B36201" }}>
                  <div style={{ width: '24px', height: '24px', flexShrink: 0 }}>
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                      <path fillRule="evenodd" clipRule="evenodd" d="M19.3319 19.2928C19.6519 19.2928 19.8229 19.0968 19.8959 18.9808C19.9669 18.8678 20.0659 18.6388 19.9429 18.3608L13.1329 5.44076C13.1219 5.41976 13.1109 5.39776 13.1009 5.37576C12.9429 5.04776 12.6569 4.99876 12.5009 4.99876C12.3439 4.99876 12.0589 5.04776 11.9009 5.37576L11.9008 5.37587C11.8898 5.39783 11.8788 5.41979 11.8679 5.44076L5.05785 18.3608C4.93485 18.6388 5.03285 18.8678 5.10385 18.9808C5.17685 19.0968 5.34785 19.2928 5.66785 19.2928H19.3319ZM14.9019 4.50876L21.7329 17.4708C22.5849 19.2398 21.2959 21.2908 19.3319 21.2908H5.66785C3.70485 21.2908 2.41485 19.2398 3.26685 17.4708L10.0999 4.50876C10.5839 3.50276 11.5419 2.99976 12.5009 2.99976C13.4589 2.99976 14.4169 3.50276 14.9019 4.50876ZM12.5007 14.9951C12.8867 14.9951 13.2007 14.6821 13.2007 14.2951L13.2017 9.29812C13.2017 8.91212 12.8877 8.59912 12.5017 8.59912C12.1147 8.59912 11.8017 8.91212 11.8017 9.29812L11.8007 14.2951C11.8007 14.6811 12.1147 14.9951 12.5007 14.9951ZM12.5007 16.494C12.0587 16.494 11.7017 16.852 11.7017 17.293C11.7017 17.735 12.0587 18.093 12.5007 18.093C12.9427 18.093 13.2997 17.735 13.2997 17.293C13.2997 16.852 12.9427 16.494 12.5007 16.494Z" fill="#B36201"/>
                    </svg>
                  </div>
                  <span style={{color: "#B36201"}} className="body1">Only 3 categories can be selected. Please deselect a category to add a new one.</span>
                </div>
              )}
              {errors.mixedContentCodes && <FormHelperText error>{errors.mixedContentCodes.message}</FormHelperText>}
            </FormControl>
          )}

          {parcelContent === '999' && (
            <FormControl fullWidth error={!!errors.itemCategorisationDescription} margin="normal">
              <div className="h4" style={{ marginBottom: '8px' }}>
                How would you categorise your items?
              </div>
              <Controller
                name="itemCategorisationDescription"
                control={control}
                rules={{
                  required: 'Describe the category of your items.',
                  validate: (val) => {
                    if (!val) return true;
                    if (!otherCategoryDescriptionRegex.test(val)) {
                      return "Sorry, only these special characters are allowed: & ' - /";
                    }
                    if (val.length > 120) return 'Category description must be 120 characters or less.';
                    return true;
                  }
                }}
                render={({ field, fieldState: {error} }) => (
                  <TextareaAutosize
                    {...field}
                    // maxLength={120}
                    minRows={1}
                    style={{
                      fontFamily: 'NZPSans, sans-serif',
                      fontWeight: '600',
                      lineHeight: '24px',
                      fontSize: '1rem',
                      color: 'rgba(28, 32, 35, 0.87)',
                      minHeight: '1.1876em',
                      border: !!error ? '1px solid #F9423A' : '1px solid rgba(0, 0, 0, 0.23)',
                      padding: '16px 12px',
                      borderRadius: '4px',
                      width: '100%',
                      boxSizing: 'border-box',
                      outline: 'none',
                      backgroundColor: 'transparent',
                  }}
                  />
                )}
              />
              {errors.itemCategorisationDescription && (
                <FormHelperText error>
                  {errors.itemCategorisationDescription.message}
                </FormHelperText>
              )}
            </FormControl>
          )}
        </div>
        <div className="itemDetails">
          <div className="sectionTitle h2">
            Item details
          </div>

          <div className='h3 lightText'>
            Complete the information for each item in your parcel. You can add more items later.
          </div>
          {fields.map((item, i) => {
            return <NewItemForm
              countryHasValidation={countryHasValidation}
              currentCountry={currentCountry}
              key={`item-${item.id}`}
              multiple={fields.length > 1}
              index={i} itemData={item}
              itemRef={ref => {
                itemsRef.current[i] = ref;
              }}
              errors={get(errors, `items[${i}]`, null)}
              control={control}
              trigger={trigger}
              setValue={setValue}
              setError={setError}
              isSubmitted={isSubmitted}
              clearErrors={clearErrors}
              remove={() => remove(i)}
              countryOriginRequired={isUSOrTerritory}
            />;
          })}
          {(fields.length === 20) && <div className="info-box">
            <div className="icon">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                <path fillRule="evenodd" clipRule="evenodd"
                      d="M12 4C7.589 4 4 7.589 4 12C4 16.411 7.589 20 12 20C16.411 20 20 16.411 20 12C20 7.589 16.411 4 12 4ZM12 22C6.486 22 2 17.514 2 12C2 6.486 6.486 2 12 2C17.514 2 22 6.486 22 12C22 17.514 17.514 22 12 22ZM12.0004 17.7998C11.5584 17.7998 11.2004 17.4418 11.2004 16.9998V9.99983C11.2004 9.55883 11.5584 9.19983 12.0004 9.19983C12.4414 9.19983 12.7994 9.55883 12.7994 9.99983L12.8004 16.9998C12.8004 17.4418 12.4424 17.7998 12.0004 17.7998ZM12 6C11.448 6 11 6.448 11 7C11 7.552 11.448 8 12 8C12.552 8 13 7.552 13 7C13 6.448 12.552 6 12 6Z"
                      fill="#0056F4"/>
              </svg>
            </div>
            <div className="content">
              <p>Maximum of 20 different item types per parcel.</p>
              <p className="note">
                If you have more than 20, you will need to send an additional parcel.
              </p>
            </div>
          </div>}
          <Button
            className="btnNewItem"
            disabled={fields.length >= 20}
            startIcon={<PlusIcon/>}
            fullWidth onClick={() => append(makeDefaultItem())} size={'large'} variant={'contained'} color="secondary">
            Add another item
          </Button>
        </div>
        <div className="totalsContainer">
          <div className="totalsColumn">
            <div className="totalsLabel">
              Total weight
            </div>
            <div className="totalsValue">
              {totalWeight} kg
            </div>
          </div>
          <div className="totalsColumn">
            <div className="totalsLabel">
              Total value
            </div>
            <div className="totalsValue">
              ${totalValue} NZD
            </div>
          </div>
        </div>

        <div className="sectionTitle h2">
          Terms and conditions
        </div>
        {isEU ? (
          <div className="confirmationContainer" key={`terms-${currentCountry || 'none'}`}>
            <div className="confirmation1">
              <FormControl error={!!errors.agreeTAndC1}>
                <Controller
                  name="agreeTAndC1"
                  control={control}
                  shouldUnregister
                  rules={{required: 'Please agree to the terms'}}
                  render={({field}) => (
                    <Checkbox
                      icon={<CheckBoxOutlineBlankIcon fontSize="small"/>}
                      checkedIcon={<CheckBoxIcon fontSize="small"/>}
                      checked={!!field.value}
                      onChange={(event) => field.onChange(event.target.checked)}
                      inputProps={{'aria-label': 'select item'}}
                    />
                  )}
                />
              </FormControl>
              <div className="confirmationText">
                <>
                  I{!isEmpty(fromFullName) && `, ${fromFullName},`} certify that the particulars given in this Customs declaration are correct and that the item does not contain any <a target="_blank" href="https://www.nzpost.co.nz/personal/sending-internationally/restricted-prohibited-items">dangerous article prohibited by legislation or by postal or Customs regulations</a>.
                </>
              </div>
            </div>

            <div className="confirmation2">
              <FormControl error={!!errors.agreeTAndC2}>
                <Controller
                  name="agreeTAndC2"
                  control={control}
                  shouldUnregister
                  rules={{required: 'Please agree to the terms'}}
                  render={({field}) => (
                    <Checkbox
                      icon={<CheckBoxOutlineBlankIcon fontSize="small"/>}
                      checkedIcon={<CheckBoxIcon fontSize="small"/>}
                      checked={!!field.value}
                      onChange={(event) => field.onChange(event.target.checked)}
                      inputProps={{'aria-label': 'select item'}}
                    />
                  )}
                />
              </FormControl>

              <div className={"confirmationText"}>
                <>
                  I agree to the <a target="_blank" href="https://www.nzpost.co.nz/about-us/who-we-are/terms-conditions/international-terms-conditions">International terms & conditions</a>.
                </>
              </div>
            </div>

            <div className="confirmation3">
              <FormControl error={!!errors.agreeTAndC3EU}>
                <Controller
                  name="agreeTAndC3EU"
                  control={control}
                  shouldUnregister
                  rules={{required: 'Please agree to the terms'}}
                  render={({field}) => (
                    <Checkbox
                      icon={<CheckBoxOutlineBlankIcon fontSize="small"/>}
                      checkedIcon={<CheckBoxIcon fontSize="small"/>}
                      checked={!!field.value}
                      onChange={(event) => field.onChange(event.target.checked)}
                      inputProps={{'aria-label': 'select item'}}
                    />
                  )}
                />
              </FormControl>

              <div className={"confirmationText"}>
                I understand that items sent to the European Union are subject to additional taxes, duties, or charges. If these are not prepaid by the sender, the receiver must pay for them. <a target="_blank" href="https://www.nzpost.co.nz/contact-support/international-delivery-updates#eu">Find out more</a>.
              </div>
            </div>
          </div>
        ) : (
          <div className="confirmationContainer" key={`terms-${currentCountry || 'none'}`}>
            <div className="confirmation1">
              <FormControl error={!!errors.agreeTAndC1}>
                <Controller
                  name="agreeTAndC1"
                  control={control}
                  shouldUnregister
                  rules={{required: 'Please agree to the terms'}}
                  render={({field}) => (
                    <Checkbox
                      // classname=""
                      icon={<CheckBoxOutlineBlankIcon fontSize="small"/>}
                      checkedIcon={<CheckBoxIcon fontSize="small"/>}
                      checked={!!field.value}
                      onChange={(event) => field.onChange(event.target.checked)}
                      // error={!!errors.agreeTAndC2}
                      inputProps={{'aria-label': 'select item'}}
                    />
                  )}
                />
              </FormControl>
              <div className="confirmationText">
                  <>
                    I{!isEmpty(fromFullName) && `, ${fromFullName},`} certify that the particulars given in this Customs
                    declaration are correct and that
                    the item does not contain any <a target="_blank"
                                                     href="https://www.nzpost.co.nz/personal/sending-internationally/restricted-prohibited-items">dangerous
                    article prohibited by legislation or by postal or Customs
                    regulations</a>.
                  </>
              </div>

            </div>
            {/*{errors.agreeTAndC1 && <FormHelperText>{errors.agreeTAndC1.message}</FormHelperText>}*/}

            <div className="confirmation2">
              <FormControl error={!!errors.agreeTAndC2}>
                <Controller
                  name="agreeTAndC2"
                  control={control}
                  shouldUnregister
                  rules={{required: 'Please agree to the terms'}}
                  render={({field}) => (
                    <Checkbox
                      icon={<CheckBoxOutlineBlankIcon fontSize="small"/>}
                      checkedIcon={<CheckBoxIcon fontSize="small"/>}
                      checked={!!field.value}
                      onChange={(event) => field.onChange(event.target.checked)}
                      // error={!!errors.agreeTAndC2}
                      inputProps={{'aria-label': 'select item'}}
                    />
                  )}
                />
              </FormControl>

              <div className={"confirmationText"}>
                  <>
                    I agree to the <a target="_blank"
                                      href="https://www.nzpost.co.nz/about-us/who-we-are/terms-conditions/international-terms-conditions">International
                    terms & conditions</a>.
                  </>
              </div>
            </div>

            {isUSOrTerritory && <div className="confirmation3">
              <FormControl error={!!errors.agreeTAndCUSA}>
                <Controller
                  name="agreeTAndCUSA"
                  control={control}
                  shouldUnregister
                  rules={{required: 'Please agree to the terms'}}
                  render={({field}) => (
                    <Checkbox
                      // classname=""
                      icon={<CheckBoxOutlineBlankIcon fontSize="small"/>}
                      checkedIcon={<CheckBoxIcon fontSize="small"/>}
                      checked={!!field.value}
                      onChange={(event) => field.onChange(event.target.checked)}
                      // error={!!errors.agreeTAndC2}
                      inputProps={{'aria-label': 'select item'}}
                    />
                  )}
                />
              </FormControl>

              <div className={"confirmationText"}>
                I agree to the Sending to <a target="_blank" href="https://nzp.st/us-sending-tnc"> US and US territories terms & conditions</a>.
              </div>
            </div>}
          </div>
        )}

        {submissionHasFailed && <Error error={"We ran into a problem. Please try sending your information again. " +
          "If you continue to get this message, talk to a staff member."}/>}
        <Button className="sendForPrinting" fullWidth type="submit" size={'large'} variant={'contained'}
                color="secondary">
          Complete form
        </Button>
      </form>

      {/*<div>*/}
      {/*  <Button size={'small'} variant={'outlined'} color="secondary">*/}
      {/*    Back*/}
      {/*  </Button>*/}
      {/*  <Button endIcon={<KeyboardArrowRightIcon />}  size={'small'} variant={'contained'} color="secondary">*/}
      {/*    Next: Confirm & send*/}
      {/*  </Button>*/}
      {/*</div>*/}

    </div>
  );
};

export default WizardPage;
